import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Loader2, ArrowLeft } from "lucide-react";
import HeaderNav from "../components/HeaderNav";
import AcceleratorPaywall from "../components/AcceleratorPaywall";
import Stepper from "../components/accelerator/Stepper";
import AccelSummary from "../components/accelerator/AccelSummary";
import StepProfile from "../components/accelerator/StepProfile";
import StepCapabilities from "../components/accelerator/StepCapabilities";
import StepRoutes from "../components/accelerator/StepRoutes";
import StepDirection from "../components/accelerator/StepDirection";
import StepBlueprint from "../components/accelerator/StepBlueprint";
import StepValidate from "../components/accelerator/StepValidate";

const AcceleratorPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [step, setStep] = useState(null);
  const [membership, setMembership] = useState(null);
  const loadMembership = useCallback(() => api.get("/membership").then((r) => setMembership(r.data)).catch(() => {}), []);

  const load = useCallback(async (keepStep = true) => {
    const { data } = await api.get("/accelerator/state");
    setState(data);
    // Always land on Step 1 on a fresh visit: history stays visible, and a redo starts from the top.
    setStep((current) => (keepStep && current) || "profile");
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
    else if (user) { load().catch(() => setState({ access: false, error: true })); loadMembership(); }
  }, [loading, user, navigate, load, loadMembership]);

  const patch = (partial) => setState((prev) => ({ ...prev, ...partial }));
  const go = (next) => { setStep(next); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (loading || !state) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <HeaderNav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-8 space-y-5" data-testid="accelerator-page">
        <button onClick={() => navigate("/app")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 py-1" data-testid="accelerator-back">
          <ArrowLeft className="w-4 h-4" /> Back to Career Intelligence
        </button>

        <header>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">2nd Income Accelerator</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">You've already spent years building skills. Let's see what those skills could potentially be monetised into — alongside your primary job, and tested before you commit.</p>
        </header>

        {!state.access ? (
          <AcceleratorPaywall membership={membership} onRefresh={loadMembership} />
        ) : (
          <>
            <AccelSummary state={state} />
            <Stepper step={step} progress={state.progress || {}} onStep={go} />

            {step === "profile" && state.history?.length > 0 && (
              <section className="card-soft p-4" data-testid="accelerator-history">
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Your earlier runs</p>
                <ul className="mt-2 space-y-1.5">
                  {state.history.slice(0, 5).map((h) => (
                    <li key={h.id} className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-slate-600" data-testid={`accelerator-history-${h.id}`}>
                      <span className="break-words">{h.filename || "Earlier resume"}{h.resume_id === state.profile?.resume_id ? " · this resume" : ""}</span>
                      <span className="num-display shrink-0 text-slate-400">{new Date(h.created_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-slate-500">Each run is kept against the resume it was built from. Starting again never mixes the two.</p>
              </section>
            )}

            {step === "profile" && <StepProfile state={state} onSaved={async (data) => {
              if (!data?.profile) { await load(true); return; }   // restart: stay on step 1 with a clean slate
              await load(true);                                    // re-read state so a resume switch drops old run data
              go("capabilities");
            }} />}
            {step === "capabilities" && <StepCapabilities state={state} patch={patch} onNext={() => go("routes")} />}
            {step === "routes" && <StepRoutes state={state} patch={patch} onNext={() => go("direction")} />}
            {step === "direction" && <StepDirection state={state} patch={patch} onNext={() => go("blueprint")} />}
            {step === "blueprint" && <StepBlueprint state={state} patch={patch} onNext={() => go("validate")} />}
            {step === "validate" && <StepValidate state={state} patch={patch} />}

            <p className="text-xs text-slate-500" data-testid="accelerator-footer-disclaimer">{state.config?.disclaimer}</p>
          </>
        )}
      </main>
    </div>
  );
};

export default AcceleratorPage;
